import { TRAILER_PAYLOAD_KG } from '../../utils/transportCostAllocation';
import type { LoadGroup, LoadQuantity } from './transportLoadTable';

/**
 * The truck card's trailer picture (owner 2026-10-06): one segment per
 * unloading stop (customer order, route order) and one stock part („Na magazyn”, or „Nadwyżka” beyond the customers), built
 * from the „Ładunek” groups. Weights are catalog piece weights × pieces;
 * the payload limit is TRAILER_PAYLOAD_KG (display only, never blocks).
 */
export interface TrailerGoods {
  name: string;
  /** Formatted dimensions of the goods (tooltip only); lines of one name differ by them. */
  dimensions: string | null;
  quantity: LoadQuantity | null;
}

export interface TrailerSegment {
  key: string;
  kind: 'order' | 'stock';
  /** The ZD rest beyond the customers (owner 2026-10-07), not a warehouse truck's load. */
  surplus: boolean;
  stop: number | null;
  orderId: string | null;
  orderNumber: string | null;
  customerName: string | null;
  deliveryAddress: string | null;
  goods: TrailerGoods[];
  /** Sum of the known weights; `weightComplete` false when a line has none. */
  weightKg: number;
  weightComplete: boolean;
  /** null when any part is unknown (no cost access, no saved share). */
  freightPLN: number | null;
  /** Some of its cargo rides on no ZD of this truck (no freight). */
  uncovered: boolean;
  /** Why the uncovered part has no freight: no ZD line pair, or the goods are not in any ZD on the truck. */
  uncoveredReason: 'unpaired' | 'exceeds' | 'noLine' | null;
}

export interface TrailerLoad {
  segments: TrailerSegment[];
  capacityKg: number;
  loadedKg: number;
  /** Goods without a catalog weight: the load is „at least” loadedKg. */
  missingWeightNames: string[];
  remainingKg: number;
  overCapacity: boolean;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * `stopByOrderId`: the unloading stop number of each customer order — the
 * same numbering as „Trasa” (transportDeliveryStops.buildDeliveryStops), so
 * the trailer, the table and the route never disagree (review 2026-10-06).
 */
export function buildTrailerLoad(groups: LoadGroup[], stopByOrderId: Map<string, number>): TrailerLoad {
  const segments = new Map<string, TrailerSegment>();
  const stopOf = stopByOrderId;
  const missing = new Set<string>();

  for (const group of groups) {
    for (const recipient of group.recipients) {
      const key = recipient.kind === 'stock' ? 'stock' : `order:${recipient.orderId}`;
      let segment = segments.get(key);
      if (!segment) {
        segment = {
          key,
          kind: recipient.kind,
          surplus: recipient.surplus === true,
          stop: recipient.orderId ? stopOf.get(recipient.orderId) ?? null : null,
          orderId: recipient.orderId,
          orderNumber: recipient.orderNumber,
          customerName: recipient.customerName,
          deliveryAddress: recipient.deliveryAddress,
          goods: [],
          weightKg: 0,
          weightComplete: true,
          freightPLN: 0,
          uncovered: false,
          uncoveredReason: null,
        };
        segments.set(key, segment);
      }
      const dimensions = group.dimensions || null;
      const same = segment.goods.find((goods) => goods.name === group.productName && goods.dimensions === dimensions && goods.quantity?.unit === recipient.quantity?.unit);
      if (same && same.quantity && recipient.quantity) same.quantity = { ...same.quantity, value: same.quantity.value + recipient.quantity.value };
      else segment.goods.push({ name: group.productName, dimensions, quantity: recipient.quantity });
      if (recipient.weightKg === null) {
        segment.weightComplete = false;
        missing.add(group.productName);
      } else {
        segment.weightKg = round2(segment.weightKg + recipient.weightKg);
      }
      if (!group.supplierOrder) {
        segment.uncovered = true;
        // One label per segment, the fixable reason first: „bez pary”, then „ponad ZD”.
        const reason = group.missing ?? 'noLine';
        const rank = { unpaired: 0, exceeds: 1, noLine: 2 } as const;
        if (segment.uncoveredReason === null || rank[reason] < rank[segment.uncoveredReason]) segment.uncoveredReason = reason;
      }
      // Uncovered cargo carries no freight (0, not unknown); a covered line without a figure makes the sum unknown.
      if (group.supplierOrder) segment.freightPLN = recipient.freightPLN === null || segment.freightPLN === null ? null : round2(segment.freightPLN + recipient.freightPLN);
    }
  }

  const ordered = Array.from(segments.values()).sort((a, b) => (a.stop ?? Number.MAX_SAFE_INTEGER) - (b.stop ?? Number.MAX_SAFE_INTEGER));
  const loadedKg = round2(ordered.reduce((sum, segment) => sum + segment.weightKg, 0));
  return {
    segments: ordered,
    capacityKg: TRAILER_PAYLOAD_KG,
    loadedKg,
    missingWeightNames: Array.from(missing),
    remainingKg: round2(TRAILER_PAYLOAD_KG - loadedKg),
    overCapacity: loadedKg > TRAILER_PAYLOAD_KG,
  };
}

export interface FitResult {
  addedKg: number;
  fits: boolean;
  /** Left after adding (negative = over by that much). */
  leftKg: number;
  /** Most whole pieces of this weight that still fit. */
  maxPieces: number;
}

/** „Czy jeszcze wejdzie?” — weight only (no floor plan, no LDM). Not on the truck card yet (owner 2026-10-06: to be placed elsewhere). */
export function fitOnTrailer(remainingKg: number, pieceWeightKg: number, pieces: number): FitResult | null {
  if (!(pieceWeightKg > 0) || !(pieces >= 0)) return null;
  const addedKg = round2(pieceWeightKg * pieces);
  const leftKg = round2(remainingKg - addedKg);
  return { addedKg, fits: leftKg >= 0, leftKg, maxPieces: Math.max(0, Math.floor(remainingKg / pieceWeightKg + 1e-9)) };
}

/** Colour of a stop on the trailer and in the „Ładunek” table (CSS `--trailer-*`). */
export type TrailerTone = 'stock' | 'uncovered' | `stop-${number}`;

export function trailerTone(segment: Pick<TrailerSegment, 'kind' | 'uncovered' | 'stop'>): TrailerTone {
  if (segment.kind === 'stock') return 'stock';
  if (segment.uncovered) return 'uncovered';
  return `stop-${((segment.stop ?? 1) - 1) % 5}`;
}

/** Tone of each customer order on the truck, for the table rows. */
export function trailerToneByOrderId(load: TrailerLoad): Map<string, TrailerTone> {
  return new Map(load.segments.filter((segment) => segment.orderId).map((segment) => [segment.orderId as string, trailerTone(segment)]));
}

/** Stop number of each customer order on the truck (route order). */
export function trailerStopByOrderId(load: TrailerLoad): Map<string, number> {
  return new Map(load.segments.filter((segment) => segment.orderId && segment.stop).map((segment) => [segment.orderId as string, segment.stop as number]));
}
